import { WelcomeSection } from "./WelcomSection";
import { PopularCase } from "./PopularCase";
import { LatestReport } from "./LatestReportCard";
import { FeatureCard } from "./components/FeatureCard";

import comingUp1 from "../../assets/images/homepage/1.svg";
import comingUp2 from "../../assets/images/homepage/2.svg";
import comingUp3 from "../../assets/images/homepage/3.svg";
import comingUp4 from "../../assets/images/homepage/4.svg";
import { useAuth } from "src/contexts/AuthContext";
import React from "react";
import { BackendReport } from "./types/type";
import WeeklyContestCard from "./WeeklyContestCard";

interface HomePageContentProps {
  userReport: BackendReport;
  companyName?: string;
}

const HomePageContent: React.FC<HomePageContentProps> = ({
  userReport,
  companyName,
}) => {
  const { userDoc } = useAuth();

  // Derive user properties with defaults
  const userName = userDoc?.survey?.name || "User";

  // Calculate total available interviews (non-expired free interviews + premium interviews)
  const freeInterviewCount = userDoc?.interviewCount || 0;

  // Calculate bought interviews from array of bought items
  const now = new Date();
  const boughtInterviewsArray = userDoc?.boughtInterviews || [];
  const boughtInterviewCount = boughtInterviewsArray.reduce(
    (
      total: number,
      item: { count?: number; expiresAt?: { _seconds: number } }
    ) => {
      // Only count non-expired items
      const expiresAt = item.expiresAt?._seconds
        ? new Date(item.expiresAt._seconds * 1000)
        : null;

      if (!expiresAt || expiresAt > now) {
        return total + (item.count || 0);
      }
      return total;
    },
    0
  );

  const totalInterviewCount = freeInterviewCount + boughtInterviewCount;

  // Calculate total available reports (non-expired free reports + premium reports)
  const freeReportCount = userDoc?.reportCount || 0;

  // Calculate bought reports from array of bought items
  const boughtReportsArray = userDoc?.boughtReports || [];
  const boughtReportCount = boughtReportsArray.reduce(
    (
      total: number,
      item: { count?: number; expiresAt?: { _seconds: number } }
    ) => {
      // Only count non-expired items
      const expiresAt = item.expiresAt?._seconds
        ? new Date(item.expiresAt._seconds * 1000)
        : null;

      if (!expiresAt || expiresAt > now) {
        return total + (item.count || 0);
      }
      return total;
    },
    0
  );

  const totalReportCount = freeReportCount + boughtReportCount;

  // Check if user has premium features
  const hasBoughtInterview =
    userDoc?.boughtInterview ||
    userDoc?.subscription?.status === "ACTIVE" ||
    userDoc?.subscriptionStatus === "ACTIVE" ||
    boughtInterviewCount > 0 ||
    false;

  const hasReportAccess =
    userDoc?.reportInterview ||
    userDoc?.subscription?.status === "ACTIVE" ||
    userDoc?.subscriptionStatus === "ACTIVE" ||
    boughtReportCount > 0 ||
    false;

  const features = [
    {
      title: "Customized Learning: News, Quizzes, & Insights",
      description:
        " Get tailored industry news, interactive quizzes to test your understanding, stay ahead of trends, and gain insights to prepare for your dream role effectively.",
      icon: <img src={comingUp1} />,
    },
    {
      title: 'Peer Ranking & "Hustle" Metric',
      description:
        'Transform your job search into a stock market of potential. Track your progress, compare with peers, and "invest" in yourself or others by bidding on future career success and outcomes.',
      icon: <img src={comingUp2} />,
    },
    {
      title: "Job Search weekly / Monthly Report",
      description:
        "Receive detailed weekly and monthly reports on applications, interviews, and responses. Gain personalized tips to refine your strategy and land more opportunities.",
      icon: <img src={comingUp3} />,
    },
    {
      title: "Cold Email Assistant",
      description:
        "Easily send AI-generated, personalized emails or LinkedIn messages to recruiters and hiring managers. Automate outreach with professional content and smart follow-ups.",
      icon: <img src={comingUp4} />,
    },
  ];

  return (
    <div className="w-full">
      {userDoc && (
        <main className="mx-auto min-h-screen">
          {/* 1) Hero / Welcome Section - No content-visibility which causes unwanted scaling */}
          <WelcomeSection
            companyName={companyName}
            name={userName}
            interviewCount={totalInterviewCount}
            reportCount={totalReportCount}
            boughtInterview={hasBoughtInterview}
            reportInterview={hasReportAccess}
            freeInterviewCount={freeInterviewCount}
            boughtInterviewCount={boughtInterviewCount}
            freeReportCount={freeReportCount}
            boughtReportCount={boughtReportCount}
          />

          {/* 2) Row of 3 cards */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <PopularCase />
            <LatestReport
              report={
                userReport
                  ? {
                      sessionId: userReport.id,
                      caseName: userReport.data.metadata?.title,
                      overallScore:
                        userReport.data.performance_summary?.overallPerformance
                          ?.score,
                      timeUsed:
                        userReport.data.metadata?.time.minutes + " mins",
                    }
                  : undefined
              }
            />
            <WeeklyContestCard />
          </div>

          {/* 3) Coming Features */}
          <section className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Coming Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </div>
          </section>
        </main>
      )}
    </div>
  );
};

export default HomePageContent;
